/**
 * The bounded back-walk behind "jump to a pinned message" (ISSUE-1054).
 * When a chosen pin is not in the loaded page, the conversation pane reads
 * older pages one at a time from its own older cursor until the message
 * shows up, history ends, a read fails, or the page cap is reached.
 *
 * The cap resolves ISSUE-1054's planner unknown: 10 pages of 50 reach 500
 * messages past the loaded page, so no "around this message" cursor on
 * `/message/list` is needed. The caller passes `fetchPage`; this module
 * never calls the SDK and renders nothing.
 */

/** Most older pages one jump reads before it gives up (ISSUE-1054). */
export const JUMP_BACK_WALK_MAX_PAGES = 10;
/** Page size for walk reads; messaging-api clamps to MAX_PAGE_LIMIT = 50. */
export const JUMP_BACK_WALK_PAGE_LIMIT = 50;

export const JUMP_SEARCHING_COPY = "Finding that message";
export const JUMP_TOO_FAR_BACK_COPY =
  "That message is too far back to jump to. Scroll up to find it.";
export const JUMP_NOT_FOUND_COPY = "Couldn't find that message in this chat.";
export const JUMP_NOTE_DISMISS_LABEL = "Dismiss";

export type typeJumpNote = "searching" | "tooFarBack" | "notFound";

export type typeJumpWalkPage<Row extends { messageId: string }> = {
  items: Row[];
  nextCursor: string | null;
};

export type typeJumpWalkResult<Row extends { messageId: string }> =
  | { status: "found"; olderRows: Row[]; nextCursor: string | null }
  | { status: "exhausted" }
  | { status: "capReached" }
  | { status: "failed" };

/**
 * Reads older pages from `startCursor`, one at a time, never in parallel.
 * `fetchPage` returning `null` means the read failed and the caller already
 * showed the error. A thrown error is not caught here. On `found`,
 * `olderRows` holds every row read, newest first, the order
 * `appendOlderPage` expects.
 */
export async function walkBackToMessage<
  Row extends { messageId: string },
>(input: {
  messageId: string;
  startCursor: string;
  maxPages: number;
  fetchPage: (cursor: string) => Promise<typeJumpWalkPage<Row> | null>;
}): Promise<typeJumpWalkResult<Row>> {
  let cursor = input.startCursor;
  const olderRows: Row[] = [];
  let pagesRead = 0;

  while (pagesRead < input.maxPages) {
    const page = await input.fetchPage(cursor);
    pagesRead += 1;
    if (page === null) return { status: "failed" };

    olderRows.push(...page.items);
    if (page.items.some((row) => row.messageId === input.messageId)) {
      return { status: "found", olderRows, nextCursor: page.nextCursor };
    }
    if (page.nextCursor === null) return { status: "exhausted" };
    cursor = page.nextCursor;
  }

  return { status: "capReached" };
}
